/**
 * Browser-global emitter for graphql-ws subscription transport lifecycle.
 *
 * The Apollo Client doesn't surface WebSocket reconnects to consumers, but
 * consumers (notably `use-mcp-chat`) sometimes need to know when the WS
 * resubscribes after a drop — graphql-ws does not replay events that were
 * published while the socket was disconnected, so anything that depends on
 * a subscription needs an out-of-band signal to re-probe after a reconnect.
 *
 * `createApolloClient` registers `on:` callbacks against the graphql-ws
 * client and dispatches matching events into this emitter. Consumers
 * subscribe with `wsLifecycleEmitter.addEventListener("connected", ...)`
 * and decide what to do on the reconnect edge (typically: track whether a
 * `closed` happened first, then fire a refetch / probe on the next
 * `connected`).
 *
 * Module-level singleton because there is at most one Apollo Client per
 * SPA in this codebase. SSR is not a concern — apollo-link-ws is
 * browser-only by construction.
 */
export type WsLifecycleEventName = "connecting" | "connected" | "closed" | "error";

class WsLifecycleEmitter extends EventTarget {
  emit(eventName: WsLifecycleEventName, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
}

export const wsLifecycleEmitter = new WsLifecycleEmitter();
